import { Button, H6, Icon, Switch } from "@sellar-io/components";
import {
  DeliveredBySellarFilterOption,
  useDiscoveryV2SearchStore,
} from "../DiscoveryV2SearchStore";
import { FilterCard } from "../filterCards/FilterCard";
import { RadioGroup, RadioGroupOption } from "~/components";
import { useShallow } from "zustand/shallow";
import { DeliveredBySellarHeaderWithCutOffToggle } from "./DeliveredBySellarMarketHeader";

export const DeliveredBySellarOptionV2 = () => {
  const {
    isDeliveredBySellarEnabled,
    filterOption,
    onChangeOption,
    onToggleDeliveredBySellar,
  } = useDeliveredBySellarOption();

  if (!isDeliveredBySellarEnabled) {
    return (
      <FilterCard className="flex flex-col gap-2 border border-zinc-300 bg-[#E8D3F454]">
        <div className="flex flex-col gap-1 text-sm">
          <div className="flex gap-1 font-semibold text-zinc-800">
            Delivered by{" "}
            <span className="inline-flex items-center gap-1 text-[#8C23C7]">
              Sellar <Icon icon="SELLAR_DISTRIBUTION_TRUCK" />
            </span>
          </div>
          <p className="text-xs text-zinc-600">
            <strong>New!</strong> Build your order with a range of makers and
            get it all delivered together by Sellar
          </p>
        </div>
        <Button
          color="slim"
          onClick={() => {
            onToggleDeliveredBySellar(true);
          }}
          className="w-full bg-[#A34FD2] text-white hover:bg-[#8b39b7] hover:text-white"
        >
          <Icon icon="STAR_SHINE" />
          Try it
        </Button>
      </FilterCard>
    );
  }

  return (
    <FilterCard className="flex flex-col border border-zinc-300 bg-[#E8D3F454] p-0">
      <div className="flex items-center gap-3 border-b border-[#E8D3F4] p-4">
        <Switch
          checked={isDeliveredBySellarEnabled}
          onChange={onToggleDeliveredBySellar}
          className="data-checked:bg-[#8C23C7]"
        />
        <div className="flex flex-col text-sm">
          <div className="flex gap-1 font-semibold text-zinc-800">
            Delivered by{" "}
            <span className="inline-flex items-center gap-1 text-[#8C23C7]">
              Sellar <Icon icon="SELLAR_DISTRIBUTION_TRUCK" />
            </span>
          </div>
          <p className="text-xs text-zinc-600">
            Build your order with a range of makers and get it all delivered
            together by Sellar
          </p>
        </div>
      </div>
      <div className="p-4">
        <RadioGroup
          value={filterOption}
          onChange={(value) => {
            onChangeOption(value as DeliveredBySellarFilterOption);
          }}
        >
          <div className="flex flex-col gap-2">
            {filterOptions.map((option) => (
              <RadioGroupOption
                key={option.value}
                value={option.value}
                className="bg-transparent p-0 text-sm"
                checkedRadioClassName="bg-[#A34FD2]"
              >
                {option.label}
              </RadioGroupOption>
            ))}
          </div>
        </RadioGroup>
      </div>
    </FilterCard>
  );
};

export const DeliveredBySellarOptionV2Mobile = () => {
  const {
    isDeliveredBySellar,
    filterOption,
    canShowSingleSupplierHeader,
    onChangeOption,
    setIsDeliveredBySellar,
  } = useDeliveredBySellarOption();

  return (
    <div className="flex flex-col">
      <div
        className={
          "flex items-center gap-3 border-y border-[#E8D3F4] bg-[#E8D3F454] p-4"
        }
      >
        <Switch
          checked={isDeliveredBySellar}
          onChange={(checked) => setIsDeliveredBySellar(checked)}
          className="data-checked:bg-[#8C23C7]"
        />
        <H6>
          Delivered by{" "}
          <span className="font-semibold text-[#8C23C7]">Sellar</span>
        </H6>
      </div>
      {!canShowSingleSupplierHeader && (
        <div className="border-b border-[#E8D3F4] bg-[#E8D3F454] p-4">
          <RadioGroup
            value={filterOption}
            onChange={(value) => {
              onChangeOption(value as DeliveredBySellarFilterOption);
            }}
          >
            <div className="flex flex-col gap-2">
              {filterOptions.map((option) => (
                <RadioGroupOption
                  key={option.value}
                  value={option.value}
                  className="bg-transparent p-0 text-sm"
                  checkedRadioClassName="bg-[#A34FD2]"
                >
                  <div className="flex w-full flex-col gap-2">
                    {option.label}
                    {option.value === "weekend" && (
                      <DeliveredBySellarHeaderWithCutOffToggle
                        isMobile={true}
                        inputContainerClassName="w-full bg-[#E8D3F4] border border-[#D1A7E9] text-zinc-700"
                      />
                    )}
                  </div>
                </RadioGroupOption>
              ))}
            </div>
          </RadioGroup>
        </div>
      )}
    </div>
  );
};

const filterOptions: Array<{
  label: string;
  value: DeliveredBySellarFilterOption;
}> = [
  { label: "All delivered by Sellar products", value: "all" },
  { label: "Get it by the weekend", value: "weekend" },
];

const useDeliveredBySellarOption = () => {
  const {
    isDeliveredBySellar,
    deliveredBySellarOption,
    selectedSuppliers,
    setIsDeliveredBySellar,
    setDeliveredBySellarOption,
    setOrderCutOffAt,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      isDeliveredBySellar: state.isDeliveredBySellar,
      deliveredBySellarOption: state.deliveredBySellarOption,
      selectedSuppliers: state.selectedSuppliers,
      setIsDeliveredBySellar: state.setIsDeliveredBySellar,
      setDeliveredBySellarOption: state.setDeliveredBySellarOption,
      setOrderCutOffAt: state.setOrderCutOffAt,
    })),
  );

  const onChangeOption = (value: DeliveredBySellarFilterOption) => {
    if (value === "all") {
      setOrderCutOffAt(undefined);
    }
    setDeliveredBySellarOption(value);
  };

  const onToggleDeliveredBySellar = (checked: boolean) => {
    if (checked) {
      setIsDeliveredBySellar(true);
    } else {
      setIsDeliveredBySellar(false);
      setDeliveredBySellarOption("all");
    }
  };

  const isDeliveredBySellarEnabled = !!isDeliveredBySellar;
  const filterOption = deliveredBySellarOption;

  const canShowSingleSupplierHeader =
    selectedSuppliers.length === 1 && isDeliveredBySellar;

  return {
    isDeliveredBySellar,
    isDeliveredBySellarEnabled,
    deliveredBySellarOption,
    filterOption,
    selectedSuppliers,
    canShowSingleSupplierHeader,
    setIsDeliveredBySellar,
    onChangeOption,
    onToggleDeliveredBySellar,
  };
};
